/**
 * actionEditRule テーブルの 1 レコードを UI で扱う ActionEditRuleDraft へ整形する。
 * 条件や編集内容の null 列は未設定として除外し、利用側がそのまま扱える形へ戻す。
 */
import { actionTypes } from '@0xtorch/core'
import { z } from 'zod'
import type { ActionEditRule, ActionEditRuleDraft } from '../../types'

type CreateActionEditRuleDraftParameters = {
  readonly row: ActionEditRule
}

type StringArrayCondition = NonNullable<
  ActionEditRuleDraft['conditions']['app']
>

type CrossTypeCondition = NonNullable<
  ActionEditRuleDraft['conditions']['crossType']
>

type EvidenceCondition = NonNullable<
  ActionEditRuleDraft['conditions']['evidence']
>

type TextCondition = NonNullable<ActionEditRuleDraft['conditions']['comment']>

type MathCondition = NonNullable<
  ActionEditRuleDraft['conditions']['transferIn']
>

type DecimalCondition = NonNullable<ActionEditRuleDraft['conditions']['amount']>

type TransferCondition = NonNullable<
  ActionEditRuleDraft['conditions']['transfer']
>

type TypeCondition = NonNullable<ActionEditRuleDraft['conditions']['type']>

type SubTypeCondition = NonNullable<
  ActionEditRuleDraft['conditions']['subType']
>

type TargetedEdit = NonNullable<ActionEditRuleDraft['edits']['amount']>

const stringArraySchema = z.array(z.string())

const crossTypeArraySchema = z.array(z.enum(['start', 'middle', 'end']))

const evidenceArraySchema = z.array(
  z.enum([
    'contract',
    'system-rule',
    'user-rule',
    'manual-check',
    'assistant-check',
    'none',
  ]),
)

const editableEvidenceSchema = z.enum([
  'contract',
  'system-rule',
  'user-rule',
  'manual-check',
  'none',
])

const actionTypeArraySchema = z.array(z.enum(actionTypes))

const subTypeArraySchema = z.array(z.number().int().nonnegative())

/**
 * JSON blob の配列条件を検証し、draft 用の条件オブジェクトへ変換する。
 * @param params 条件演算子、比較値、検証 schema を受け取る。
 * @returns 条件が成立していれば draft 用の条件、未設定または不正値なら undefined を返す。
 */
const createParsedArrayCondition = <
  TOperator extends string,
  TSchema extends z.ZodType<readonly unknown[]>,
>({
  operator,
  schema,
  value,
}: {
  readonly operator: TOperator | null
  readonly schema: TSchema
  readonly value: unknown
}):
  | {
      readonly operator: TOperator
      readonly value: z.infer<TSchema>
    }
  | undefined => {
  if (operator === null) {
    return undefined
  }

  const parsedValue = schema.safeParse(value)

  if (!parsedValue.success) {
    return undefined
  }

  return {
    operator,
    value: parsedValue.data,
  }
}

/**
 * 文字列配列条件の列値を draft 用の条件オブジェクトへ変換する。
 * @param params 条件演算子と比較値を受け取る。
 * @returns 条件が成立していれば draft 用の条件、未設定なら undefined を返す。
 */
const createStringArrayCondition = ({
  operator,
  value,
}: {
  readonly operator: StringArrayCondition['operator'] | null
  readonly value: unknown
}): StringArrayCondition | undefined => {
  return createParsedArrayCondition<
    StringArrayCondition['operator'],
    typeof stringArraySchema
  >({
    operator,
    schema: stringArraySchema,
    value,
  })
}

/**
 * crossType 条件の列値を draft 用の条件オブジェクトへ変換する。
 * @param params 条件演算子と比較値を受け取る。
 * @returns 条件が成立していれば draft 用の条件、未設定なら undefined を返す。
 */
const createCrossTypeCondition = ({
  operator,
  value,
}: {
  readonly operator: CrossTypeCondition['operator'] | null
  readonly value: unknown
}): CrossTypeCondition | undefined => {
  return createParsedArrayCondition<
    CrossTypeCondition['operator'],
    typeof crossTypeArraySchema
  >({
    operator,
    schema: crossTypeArraySchema,
    value,
  })
}

/**
 * evidence 条件の列値を draft 用の条件オブジェクトへ変換する。
 * @param params 条件演算子と比較値を受け取る。
 * @returns 条件が成立していれば draft 用の条件、未設定なら undefined を返す。
 */
const createEvidenceCondition = ({
  operator,
  value,
}: {
  readonly operator: EvidenceCondition['operator'] | null
  readonly value: unknown
}): EvidenceCondition | undefined => {
  return createParsedArrayCondition<
    EvidenceCondition['operator'],
    typeof evidenceArraySchema
  >({
    operator,
    schema: evidenceArraySchema,
    value,
  })
}

/**
 * transfer 条件の列値を draft 用の条件オブジェクトへ変換する。
 * @param params 条件演算子と比較値を受け取る。
 * @returns 条件が成立していれば draft 用の条件、未設定なら undefined を返す。
 */
const createTransferCondition = ({
  operator,
  value,
}: {
  readonly operator: TransferCondition['operator'] | null
  readonly value: unknown
}): TransferCondition | undefined => {
  return createParsedArrayCondition<
    TransferCondition['operator'],
    typeof stringArraySchema
  >({
    operator,
    schema: stringArraySchema,
    value,
  })
}

/**
 * 文字列条件の列値を draft 用の条件オブジェクトへ変換する。
 * @param params 条件演算子と比較値を受け取る。
 * @returns 条件が成立していれば draft 用の条件、未設定なら undefined を返す。
 */
const createTextCondition = ({
  operator,
  value,
}: {
  readonly operator: TextCondition['operator'] | null
  readonly value: string | null
}): TextCondition | undefined => {
  if (operator === null) {
    return undefined
  }

  if (value === null) {
    return {
      operator,
    }
  }

  return {
    operator,
    value,
  }
}

/**
 * 数値条件の列値を draft 用の条件オブジェクトへ変換する。
 * @param params 条件演算子と比較値を受け取る。
 * @returns 条件が成立していれば draft 用の条件、未設定なら undefined を返す。
 */
const createMathCondition = ({
  operator,
  value,
}: {
  readonly operator: MathCondition['operator'] | null
  readonly value: number | null
}): MathCondition | undefined => {
  if (operator === null || value === null) {
    return undefined
  }

  return {
    operator,
    value,
  }
}

/**
 * 10 進数文字列条件の列値を draft 用の条件オブジェクトへ変換する。
 * @param params 条件演算子と比較値を受け取る。
 * @returns 条件が成立していれば draft 用の条件、未設定なら undefined を返す。
 */
const createDecimalCondition = ({
  operator,
  value,
}: {
  readonly operator: DecimalCondition['operator'] | null
  readonly value: string | null
}): DecimalCondition | undefined => {
  if (operator === null || value === null) {
    return undefined
  }

  return {
    operator,
    value,
  }
}

/**
 * type 条件の列値を draft 用の条件オブジェクトへ変換する。
 * @param params 条件演算子と比較値を受け取る。
 * @returns 条件が成立していれば draft 用の条件、未設定なら undefined を返す。
 */
const createTypeCondition = <TOperator extends TypeCondition['operator']>({
  operator,
  value,
}: {
  readonly operator: TOperator | null
  readonly value: unknown
}): TypeCondition | undefined => {
  if (value === null) {
    return undefined
  }

  return createParsedArrayCondition<
    TypeCondition['operator'],
    typeof actionTypeArraySchema
  >({
    operator,
    schema: actionTypeArraySchema,
    value,
  })
}

/**
 * subType 条件の列値を draft 用の条件オブジェクトへ変換する。
 * @param params 条件演算子と比較値を受け取る。
 * @returns 条件が成立していれば draft 用の条件、未設定なら undefined を返す。
 */
const createSubTypeCondition = <
  TOperator extends SubTypeCondition['operator'],
>({
  operator,
  value,
}: {
  readonly operator: TOperator | null
  readonly value: unknown
}): SubTypeCondition | undefined => {
  if (value === null) {
    return undefined
  }

  return createParsedArrayCondition<
    SubTypeCondition['operator'],
    typeof subTypeArraySchema
  >({
    operator,
    schema: subTypeArraySchema,
    value,
  })
}

/**
 * amount・asset・price の編集列を draft 用の編集オブジェクトへ変換する。
 * @param params 編集値と対象指定を受け取る。
 * @returns 編集値が設定されていれば draft 用の編集、未設定なら undefined を返す。
 */
const createTargetedEdit = ({
  targetAsset,
  targetDirection,
  targetPrice,
  value,
}: {
  readonly value: string | null
  readonly targetAsset: string | null
  readonly targetDirection: TargetedEdit['targetDirection'] | null
  readonly targetPrice: TargetedEdit['targetPrice'] | null
}): TargetedEdit | undefined => {
  if (value === null) {
    return undefined
  }

  return {
    value,
    ...(targetAsset === null ? {} : { targetAsset }),
    ...(targetDirection === null ? {} : { targetDirection }),
    ...(targetPrice === null ? {} : { targetPrice }),
  }
}

/**
 * DB の actionEditRule 行を ActionEditRuleDraft へ整形する。
 * @param params 整形対象の row を受け取る。
 * @returns UI から扱いやすい draft 形式の rule を返す。
 */
export const createActionEditRuleDraft = ({
  row,
}: CreateActionEditRuleDraftParameters): ActionEditRuleDraft => {
  const amountCondition = createDecimalCondition({
    operator: row.conditionAmountOp,
    value: row.conditionAmountValue,
  })
  const app = createStringArrayCondition({
    operator: row.conditionAppOp,
    value: row.conditionAppValue,
  })
  const asset = createStringArrayCondition({
    operator: row.conditionAssetOp,
    value: row.conditionAssetValue,
  })
  const category = createStringArrayCondition({
    operator: row.conditionCategoryOp,
    value: row.conditionCategoryValue,
  })
  const comment = createTextCondition({
    operator: row.conditionCommentOp,
    value: row.conditionCommentValue,
  })
  const crossId = createTextCondition({
    operator: row.conditionCrossIdOp,
    value: row.conditionCrossIdValue,
  })
  const crossType = createCrossTypeCondition({
    operator: row.conditionCrossTypeOp,
    value: row.conditionCrossTypeValue,
  })
  const evidence = createEvidenceCondition({
    operator: row.conditionEvidenceOp,
    value: row.conditionEvidenceValue,
  })
  const evmFunction = createTextCondition({
    operator: row.conditionEvmFunctionOp,
    value: row.conditionEvmFunctionValue,
  })
  const evmTo = createTextCondition({
    operator: row.conditionEvmToOp,
    value: row.conditionEvmToValue,
  })
  const loanId = createTextCondition({
    operator: row.conditionLoanIdOp,
    value: row.conditionLoanIdValue,
  })
  const transfer = createTransferCondition({
    operator: row.conditionTransferOp,
    value: row.conditionTransferValue,
  })
  const transferIn = createMathCondition({
    operator: row.conditionTransferInOp,
    value: row.conditionTransferInValue,
  })
  const transferOut = createMathCondition({
    operator: row.conditionTransferOutOp,
    value: row.conditionTransferOutValue,
  })
  const subType = createSubTypeCondition({
    operator: row.conditionSubTypeOp,
    value: row.conditionSubTypeValue,
  })
  const type = createTypeCondition({
    operator: row.conditionTypeOp,
    value: row.conditionTypeValue,
  })
  const amount = createTargetedEdit({
    value: row.editAmountValue,
    targetAsset: row.editAmountTargetAsset,
    targetDirection: row.editAmountTargetDirection,
    targetPrice: row.editAmountTargetPrice,
  })
  const editAsset = createTargetedEdit({
    value: row.editAssetValue,
    targetAsset: row.editAssetTargetAsset,
    targetDirection: row.editAssetTargetDirection,
    targetPrice: row.editAssetTargetPrice,
  })
  const price = createTargetedEdit({
    value: row.editPriceValue,
    targetAsset: row.editPriceTargetAsset,
    targetDirection: row.editPriceTargetDirection,
    targetPrice: row.editPriceTargetPrice,
  })

  return {
    id: row.id,
    name: row.name,
    priority: row.priority,
    stopped: row.stopped ?? false,
    conditions: {
      ...(amountCondition === undefined ? {} : { amount: amountCondition }),
      ...(app === undefined ? {} : { app }),
      ...(asset === undefined ? {} : { asset }),
      ...(category === undefined ? {} : { category }),
      ...(comment === undefined ? {} : { comment }),
      ...(crossId === undefined ? {} : { crossId }),
      ...(crossType === undefined ? {} : { crossType }),
      ...(evidence === undefined ? {} : { evidence }),
      ...(evmFunction === undefined ? {} : { evmFunction }),
      ...(evmTo === undefined ? {} : { evmTo }),
      ...(loanId === undefined ? {} : { loanId }),
      ...(transfer === undefined ? {} : { transfer }),
      ...(transferIn === undefined ? {} : { transferIn }),
      ...(transferOut === undefined ? {} : { transferOut }),
      ...(subType === undefined ? {} : { subType }),
      ...(type === undefined ? {} : { type }),
    },
    edits: {
      ...(amount === undefined ? {} : { amount }),
      ...(editAsset === undefined ? {} : { asset: editAsset }),
      ...(row.editCommentValue === null
        ? {}
        : { comment: row.editCommentValue }),
      ...(row.editCrossIdValue === null
        ? {}
        : { crossId: row.editCrossIdValue }),
      ...(row.editCrossTypeValue === null
        ? {}
        : { crossType: row.editCrossTypeValue }),
      ...(row.editEvidenceValue === null
        ? {}
        : editableEvidenceSchema.safeParse(row.editEvidenceValue).success
          ? { evidence: editableEvidenceSchema.parse(row.editEvidenceValue) }
          : {}),
      ...(row.editLockedValue === null ? {} : { locked: row.editLockedValue }),
      ...(row.editLoanIdValue === null ? {} : { loanId: row.editLoanIdValue }),
      ...(price === undefined ? {} : { price }),
      ...(row.editSubTypeValue === null
        ? {}
        : { subType: row.editSubTypeValue }),
      ...(row.editTypeValue === null ? {} : { type: row.editTypeValue }),
    },
  }
}
